import * as React from "react";
import { Paper, CssBaseline, makeStyles, TextField } from "@material-ui/core";
import { UserContext } from "../context/UserContext";
import { Link } from "react-router-dom";
import * as yup from "yup";
import ButtonSpinner from "./ButtonSpinner";
import { useFormik } from "formik";
import { Api } from "../api";

interface IProps {
  loadingUser?: boolean;
}

const LoginSchema = yup.object({
  username: yup.string().required("Username or Email Address is required."),
});

const PasswordSchema = yup.object({
  username: yup.string().required("Username or Email Address is required."),
  password: yup.string().required("Password is required"),
});

export default function Login({ loadingUser }: IProps) {
  const user = React.useContext(UserContext);
  const [loading, setLoading] = React.useState(loadingUser ?? false);
  const [isPasswordFlow, setIsPasswordFlow] = React.useState(false);
  const classes = useStyles();

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    onSubmit: onSubmit,
    validationSchema: isPasswordFlow ? PasswordSchema : LoginSchema,
  });

  React.useEffect(() => {
    if (user.loginError && user.loginError.length > 0) {
      setLoading(false);
    }
  }, [user.loginError]);

  React.useEffect(() => {
    if (loadingUser) {
      setLoading(true);
    }
  }, [loadingUser]);

  function onSubmit(data: any) {
    setLoading(true);
    if (isPasswordFlow) {
      user.login(data.username, data.password);
    } else {
      Api.sso
        .check(data.username)
        .then((result) => {
          const isSsoUser = result?.data?.data ?? false;
          if (isSsoUser) {
            const baseURL: string =
              process.env.REACT_APP_API_BASE_URL || "localhost";
            let initiateSSO = baseURL + "/sso/auth?email=" + data.username;
            window.location.replace(initiateSSO);
          } else {
            setIsPasswordFlow(true);
          }
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => setLoading(false));
    }
  }

  return (
    <Paper className={classes.paper}>
      <CssBaseline />
      <img alt="logo" src="./empathiHR_logo.png" width={230} />
      {user.loginError && user.loginError.length > 0 && (
        <div className={classes.error}>{user.loginError}</div>
      )}
      <form className={classes.form} onSubmit={formik.handleSubmit}>
        <TextField
          id="username"
          name="username"
          label="Username or Email Address"
          variant="outlined"
          fullWidth
          autoComplete="username"
          margin="normal"
          error={
            formik.touched.username && formik.errors.username ? true : false
          }
          helperText={
            formik.touched.username && formik.errors.username
              ? formik.errors.username
              : null
          }
          onChange={formik.handleChange}
        />
        {isPasswordFlow && (
          <TextField
            id="password"
            name="password"
            label="Password"
            variant="outlined"
            fullWidth
            type="password"
            autoComplete="password"
            margin="normal"
            error={
              formik.touched.password && formik.errors.password ? true : false
            }
            helperText={
              formik.touched.password && formik.errors.password
                ? formik.errors.password
                : null
            }
            onChange={formik.handleChange}
          />
        )}

        <ButtonSpinner
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.button}
          loading={loading}
        >
          {isPasswordFlow ? "Sign In" : "Continue"}
        </ButtonSpinner>
      </form>

      {isPasswordFlow && (
        <Link to="/forgot-password" className={classes.link}>
          Forgot your password?
        </Link>
      )}
    </Paper>
  );
}

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
  },
  logo: {
    url: `url("./empathiHR_logo.png")`,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontSize: 14,
    fontWeight: 500,
    marginTop: 30,
    textTransform: "none",
  },
  main: {
    width: "auto",
    display: "block",
    marginLeft: 15,
    marginRight: 15,
  },
  orLogin: {
    width: "100%",
    textAlign: "center",
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 34,
    flexGrow: 1,
    borderRadius: 20,
  },
  avatar: {
    width: 40,
    height: 40,
    display: "flex",
    position: "relative",
    overflow: "hidden",
    fontSize: "1.25rem",
    alignItems: "center",
    flexShrink: 0,
    userSelect: "none",
    borderRadius: "50%",
    justifyContent: "center",
    backgroundColor: theme.palette.secondary.main,
  },
  button: {
    marginTop: 30,
  },
  title: {
    marginTop: 15,
    marginBottom: 24,
    fontSize: 34,
    fontWeight: 600,
  },
  error: {
    background: "#B00020",
    color: "white",
    padding: 15,
    width: "100%",
    textAlign: "center",
  },
  ssoButtonContainer: {
    border: "1px solid #1ea3f8",
    height: 50,
    width: "100%",
    borderRadius: 4,
  },
  ssoButton: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontSize: 16,
    fontWeight: 500,
    height: "100%",
    verticalAlign: "center",
    textTransform: "none",
  },
}));
